// @flow
import {
  BulkEditFormContainer,
  DataEntryRow,
  DataEntryHeaderRow,
  SelectedDataEntriesConsumer,
  // $FlowFixMe - TS file
} from 'general-ledger';
import { colors } from 'bench-style-constants';
import React, { Fragment } from 'react';
import styled from 'styled-components';
import { TextBlock } from 'bench-internal-theme';

import type { DataEntry } from './types';
import doesDataEntryContainSearchString from './doesDataEntryContainSearchString';

const bulkEditFormHeight = 164;

const IssuesViewContentArea = styled.div`
  bottom: 0;
  left: 0;
  overflow: auto;
  position: absolute;
  right: 0;
  top: 0;
  ${({ isBulkEditFormOpen }) => `padding-bottom: ${isBulkEditFormOpen ? '200px' : '32px'}`};
`;

const IssuesViewHeader = styled.div`
  margin: 32px;
`;

const IssuesViewSubHeader = styled(TextBlock)`
  width: 750px;
  padding-top: 16px;
  color: ${colors.smoke};
`;

const BulkEditFormArea = styled.div`
  background: ${colors.pearl};
  bottom: 0;
  box-shadow: 0 -1px ${colors.silt};
  height: ${bulkEditFormHeight}px;
  left: 0;
  overflow: auto;
  position: absolute;
  right: 0;
  z-index: 1;
`;

const issuesColumnHeaders = [
  { heading: 'Date', width: '80px' },
  { heading: 'Transaction Name', width: 'auto' },
  { heading: 'Debit', width: '80px', align: 'right' },
  { heading: 'Credit', width: '80px', align: 'right' },
  { heading: 'Notes', width: 'auto', maxWidth: '300px' },
  {
    heading: 'Has client comments, client categorization response, or internal notes',
    icon: 'speechBubble',
  },
  { heading: 'Client selected this ledger account', icon: 'circleCheck' },
  { heading: 'Has attachment', icon: 'paperclip' },
  { heading: 'Has flagged internal note(s)', icon: 'flagFilled' },
  { heading: 'Add-On', width: '100px' },
];

type IssuesViewProps = {
  dataEntriesWithInconsistencies: Array<DataEntry>,
  toggleDataEntry: DataEntry => any,
  search: string,
  selectedDataEntry: ?DataEntry,
};

const IssuesView = ({
  dataEntriesWithInconsistencies,
  toggleDataEntry,
  search,
  selectedDataEntry,
}: IssuesViewProps) => {
  const isSearchStringSet = search.trim() !== '';
  return (
    <SelectedDataEntriesConsumer>
      {({ selectedDataEntryIds }: any) => {
        const areAnyDataEntriesSelectedForBulkEdit = !!selectedDataEntryIds.length;
        const shownDataEntries = dataEntriesWithInconsistencies.filter(dataEntry =>
          isSearchStringSet ? doesDataEntryContainSearchString(dataEntry, search) : true,
        );
        return (
          <Fragment>
            <IssuesViewContentArea isBulkEditFormOpen={areAnyDataEntriesSelectedForBulkEdit}>
              <IssuesViewHeader>
                <TextBlock styleType="title">Transaction Issues</TextBlock>
                <IssuesViewSubHeader styleType="small">
                  The following transactions do not currently meet Bench’s bookkeeping quality
                  standards. Please review for errors and ensure that all remaining transactions
                  have a file attachment, client comment or internal note to provide justification
                  as to why.
                </IssuesViewSubHeader>
              </IssuesViewHeader>
              <DataEntryHeaderRow
                dataEntriesUnderHeader={shownDataEntries}
                columns={issuesColumnHeaders}
              />
              {shownDataEntries.map((dataEntry, index) => (
                <DataEntryRow
                  key={dataEntry.id}
                  dataEntry={dataEntry}
                  hasBackgroundColor={index % 2}
                  isSelected={!!selectedDataEntry && selectedDataEntry.id === dataEntry.id}
                  showLedgerAccountName
                  toggleDataEntry={toggleDataEntry}
                />
              ))}
            </IssuesViewContentArea>
            {areAnyDataEntriesSelectedForBulkEdit && (
              <BulkEditFormArea>
                <BulkEditFormContainer availableDataEntries={shownDataEntries} />
              </BulkEditFormArea>
            )}
          </Fragment>
        );
      }}
    </SelectedDataEntriesConsumer>
  );
};

export default IssuesView;
